import { useEffect, useMemo, useState } from 'react';
import PredictionService from '@utility/services/prediction';
import { ErrorMessageConstant } from '@utility/constants/common';
import { CacheUtility } from '@utility/cache';
import { APIPath } from '@utility/constants/api';
import { DateUtility } from '@utility/date';
import * as yup from 'yup';
import ListService from '@utility/services/list';
import { differenceInDays } from '@page-components/timeline/common';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';

export const predictionDayLeftHook = (
  itemId,
  regionId,
  destination,
  fulfillment_provider_id,
  updateItemsPrediction?: () => void,
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  const fetchData = async (refresh = false) => {
    if (!itemId) return;

    try {
      !refresh && setLoading('Loading');
      setError('');
      const result = await PredictionService.getInventoryTimeline(
        {
          item_id: itemId,
          marketplace_region_id: regionId,
          destination_id: destination,
          origin_id: fulfillment_provider_id,
        },
        refresh,
      );

      setData(result.data);
      const key = [itemId, regionId, destination, fulfillment_provider_id]
        .filter(x => x)
        .join('_');

      const lastRefreshInCache = CacheUtility.get(key)?.updatedAt;

      const minutesLastRefreshedInCache = DateUtility.difference(
        lastRefreshInCache || new Date(),
        new Date(),
        'minutes',
      );

      if (!refresh && minutesLastRefreshedInCache >= 10) {
        CacheUtility.save(key, {
          updatedAt: new Date(),
        });
        if (itemId && fulfillment_provider_id) {
          await forceRefresh();
        } else {
          await forceRefreshUPC(result.data.upc);
        }
        updateItemsPrediction && (await updateItemsPrediction());
      }
    } catch (error) {
      setError(ErrorMessageConstant.promptToChat);
    } finally {
      setLoading('');
    }
  };

  const forceRefresh = async () => {
    try {
      setLoading('Loading');
      setError('');
      await PredictionService.refreshInventoryTimeline({
        item_id: itemId,
        fulfillment_provider_id,
        addManual: false,
      });
      await fetchData(true);
    } catch (error) {
      setError(ErrorMessageConstant.promptToChat);
    } finally {
      setLoading('');
    }
  };

  const forceRefreshUPC = async upc => {
    try {
      setLoading('Loading');
      await PredictionService.refreshPurchaseOrderTimeline({ upc });
      await fetchData(true);
    } catch (error) {
      setError(ErrorMessageConstant.promptToChat);
    } finally {
      setLoading('');
    }
  };

  useEffect(() => {
    setData([]);
    if (regionId && itemId) fetchData();
    return () => {
      CacheUtility.containsRemoved(APIPath.scmInventoryTimeline);
    };
  }, [regionId, itemId]);

  useEffect(() => {
    CacheUtility.remove(APIPath.scmInventoryTimeline);
  }, [destination]);

  const softRefresh = () => fetchData(true);

  return {
    data,
    loading,
    error,
    setLoading,
    forceRefresh,
    setError,
    forceRefreshUPC,
    softRefresh,
  };
};

export const purchaseOrderPredictionDayLeftHook = (
  upc,
  regionId,
  destination,
) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);

  const fetchData = async (forceRefresh = false) => {
    setData([]);
    try {
      setLoading('Loading');
      setError('');
      const result = await PredictionService.getPurchaseOrderTimeline(
        upc,
        forceRefresh,
      );
      setData(result.data);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading('');
    }
  };

  useEffect(() => {
    fetchCountryData();
  }, []);

  const fetchCountryData = async () => {
    const data = await ListService.country();
    setCountries(data);
  };

  const getCountry = code => {
    if (countries) {
      // @ts-expect-error - TS2339: Property 'code' does not exist on type 'never'.
      return countries.find(x => x.code === code)?.name;
    }
    return code;
  };

  const hardRefresh = async () => {
    setLoading('Loading');
    await PredictionService.refreshPurchaseOrderTimeline({ upc });
    await fetchData(true);
  };

  useEffect(() => {
    setData([]);
    if (regionId) fetchData();

    return () => {
      CacheUtility.containsRemoved(APIPath.scmPurchaseOrderTimeline);
    };
  }, [regionId, upc]);

  const refresh = async () => {
    await fetchData(true);
  };

  useEffect(() => {
    CacheUtility.remove(APIPath.scmPurchaseOrderTimeline);
  }, [destination]);

  return {
    data,
    loading,
    error,
    setLoading,
    refresh,
    getCountry,
    hardRefresh,
  };
};

export const globalItemHook = (upc, destination, supplier_id) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading('Loading');
      setError('');
      const result = await PredictionService.getGlobalItem(upc, supplier_id);
      setData(result.data);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading('');
    }
  };

  const hardRefresh = async () => {
    setLoading('Loading');
    await PredictionService.refreshPurchaseOrderTimeline({ upc });
    // @ts-expect-error - TS2554: Expected 0 arguments, but got 1.
    await fetchData(true);
  };

  useEffect(() => {
    setData([]);
    if (upc && supplier_id) {
      fetchData();
    }

    return () => {
      CacheUtility.containsRemoved(APIPath.scmPurchaseOrderTimeline);
    };
  }, [upc, supplier_id]);

  const refresh = async () => {
    // @ts-expect-error - TS2554: Expected 0 arguments, but got 1.
    await fetchData(true);
  };

  useEffect(() => {
    CacheUtility.remove(APIPath.scmPurchaseOrderTimeline);
  }, [destination]);

  const marketplaceDropdown = useMemo(() => {
    let options = [];
    // @ts-expect-error - TS2339: Property 'regionalItemsList' does not exist on type 'never[]'.
    let arr = data.regionalItemsList || [];
    arr = arr.filter(x =>
      // @ts-expect-error - TS2339: Property 'timeline' does not exist on type 'never[]'.
      (data?.timeline?.regional_providers || []).find(
        y => y.region_name === x.marketplace_region_name,
      ),
    );
    arr.forEach(item => {
      // @ts-expect-error - TS2345: Argument of type '{ key: any; value: any; flag: any; name: any; text: any; regio...
      options.push({
        key: item?._id,
        value: item?._id,
        flag: item.marketplace_region_name,
        name: item.marketplace_region_name,
        text: item.marketplace_region_alias,
        regionId: item.marketplace_region_id,
      });
    });

    // @ts-expect-error - TS2322: Type '{ image: { src: string; }; flag: null; key?: string | number | undefined; ...
    options = dropdownOptionsIncludeFlag(options);

    return options;
  }, [data]);

  return {
    data,
    loading,
    error,
    refresh,
    hardRefresh,
    setLoading,
    marketplaceDropdown,
  };
};

export const useCondensedTimelineHook = ({
  destination,
  fulfillmentProvider,
}) => {
  const differenceInDays = (d1, d2) => {
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    try {
      const Difference_In_Time = date2.getTime() - date1.getTime();
      return Math.round(Difference_In_Time / (1000 * 3600 * 24));
    } catch (e) {
      return 0;
    }
  };

  const checkEntry = (entry, isPOShipment = false) => {
    if (destination && fulfillmentProvider) {
      if (
        !isPOShipment &&
        entry.origin_id &&
        fulfillmentProvider !== entry.origin_id
      )
        return null;

      if (entry.destination && destination !== entry.destination) return null;

      if (
        entry.fulfillment_provider_id &&
        ![destination, fulfillmentProvider].includes(
          entry.fulfillment_provider_id,
        )
      )
        return null;
    }

    if (!entry.startDate || !entry.endDate) return null;
    // @ts-expect-error - TS2345: Argument of type 'number' is not assignable to parameter of type 'DateType'.
    if (DateUtility.isDateAfter(Date.now(), entry.endDate)) return null;
    if (
      DateUtility.isDateAfter('1971-01-01T00:00:00.000Z', entry.startDate) ||
      DateUtility.isDateAfter('1971-01-01T00:00:00.000Z', entry.endDate)
    )
      return null;
    if (
      !entry.skip &&
      differenceInDays(new Date(entry.startDate), new Date(entry.endDate)) < 1
    )
      return null;
    if (
      differenceInDays(new Date(entry.startDate), new Date(entry.endDate)) > 365
    ) {
      return {
        ...entry,
        daysLeft:
          entry.daysLeft ||
          differenceInDays(new Date(entry.startDate), new Date(entry.endDate)),
        endDate: DateUtility.addDays(entry.startDate, 365),
        startDate: DateUtility.isDateAfter(
          new Date(),
          new Date(entry.startDate),
        )
          ? new Date()
          : entry.startDate,
      };
    }
    if (DateUtility.isDateAfter(new Date(), new Date(entry.startDate))) {
      return {
        ...entry,
        startDate: new Date(),
      };
    }
    return entry;
  };

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: false,
      speed: 100,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      swipeToSlide: false,
      variableWidth: true,
    }),
    [],
  );

  const predictionSchema = yup.object().shape({
    safe_factor: yup.number(),
    moq: yup.number(),
  });

  return {
    checkEntry,
    differenceInDays,
    settings,
    predictionSchema,
  };
};

export const useInventoryStatusHook = (allowPast = false) => {
  const margin = {
    top: 80,
    left: 200,
    right: 20,
    bottom: 20,
  };

  const checkEntry = entry => {
    if (
      DateUtility.difference(
        new Date(entry.startDate),
        new Date(entry.endDate),
      ) < 7
    )
      entry.isOverflown = true;

    if (!entry.startDate || !entry.endDate) return null;
    // @ts-expect-error - TS2345: Argument of type 'number' is not assignable to parameter of type 'DateType'.
    if (DateUtility.isDateAfter(Date.now(), entry.endDate) && !allowPast)
      return null;
    if (
      DateUtility.isDateAfter('1971-01-01T00:00:00.000Z', entry.startDate) ||
      DateUtility.isDateAfter('1971-01-01T00:00:00.000Z', entry.endDate)
    )
      return null;
    if (
      differenceInDays(new Date(entry.startDate), new Date(entry.endDate)) <= 0
    )
      return null;
    if (
      differenceInDays(new Date(entry.startDate), new Date(entry.endDate)) > 365
    )
      return {
        ...entry,
        daysLeft: differenceInDays(
          new Date(entry.startDate),
          new Date(entry.endDate),
        ),
        endDate: DateUtility.addDays(entry.startDate, 365),
      };
    return entry;
  };

  return { margin, checkEntry, differenceInDays };
};
